/**
 * SkipLink (B10) — the first element in the app (§H.1): "Skip to places".
 * Visually hidden until focused; then a glass pill at the top left.
 * Target: #ld-places (B7's listbox), else the first listbox in the panel.
 * On mobile it lifts the sheet to half first, so the list is on screen.
 */
import type { MouseEvent } from "react";
import { cx } from "@/design";
import { actions, getApp } from "@/store/store";
import { focusSafely } from "./focus";
import "./a11y.css";

/** B7's listbox id (PLACES_LISTBOX_ID in src/features/panel/PlacesPanel.tsx). */
export const SKIP_TARGET_ID = "ld-places";
export const SKIP_LABEL = "Skip to places";

function findTarget(id: string): HTMLElement | null {
  return (
    document.getElementById(id) ??
    document.querySelector<HTMLElement>('[data-ld-slot="panel"] [role="listbox"], [data-ld-slot="sheet"] [role="listbox"]') ??
    document.querySelector<HTMLElement>('[data-ld-slot="panel"] aside, [data-ld-slot="sheet"] aside')
  );
}

export function SkipLink({ targetId = SKIP_TARGET_ID, hidden = false }: { targetId?: string; hidden?: boolean }) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const s = getApp();
    if (s.layout === "mobile") {
      if (s.selectedId) actions.select(null, "key");
      if (s.sheet === "peek") actions.setSheet("half");
    }
    // After the sheet / panel has had a frame to show the list.
    requestAnimationFrame(() => {
      const t = findTarget(targetId);
      if (t && t.tabIndex < 0 && !t.hasAttribute("tabindex")) t.tabIndex = -1;
      focusSafely(t);
    });
  };
  if (hidden) return null;
  return (
    <a href={`#${targetId}`} className={cx("ld-btn", "ld-btn--glass", "ld-btn--sm", "ld-glass", "ld-loc-a11y-skip")} onClick={onClick}>
      {SKIP_LABEL}
    </a>
  );
}
